import React, { useState } from 'react'
import { BCol, BRow } from '@prague-digi/ui-components'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { change, getFormValues } from 'redux-form'

import { Address } from '../../types/codetable'
import { intl, IntlInterface } from '../../hoc'
import { BTextInput, BAutoComplete } from '../'
import messages from '../../i18n'
import { trim } from '../../utils/helpers'
import { codetableActions } from '../../redux'
import { ReduxState } from '../../types'

interface OuterProps {
  form: string
  namePrefix: string
}

interface ReduxProps {
  data: any
  change: any
  getAddressHints: any
}

type Props = OuterProps & IntlInterface & ReduxProps

interface State {
  searchedAddressText: string
  addresses: Address[]
}
const defaultState: State = {
  addresses: [],
  searchedAddressText: '',
}

let fetchAddressTimeoutRef: any
let fetchAddressRequestId: any

const msg = messages.components.address

export const formatStreet = ({
  street = '',
  landRegistryNumber = '',
  houseNumber = '',
}): string => {
  if (street) {
    if (landRegistryNumber && houseNumber) {
      return `${street} ${landRegistryNumber}/${houseNumber}`
    }
    if (landRegistryNumber || houseNumber) {
      return `${street} ${landRegistryNumber || houseNumber}`
    }

    return street
  }
  return ''
}

export const getValue = ({
  fieldName,
  formValues,
  namePrefix,
  searchText,
}: {
  namePrefix: string
  formValues: any
  fieldName: string
  searchText: string
}): string => {
  const { houseNumber, landRegistryNumber, street } = formValues[namePrefix]

  // we don't' have in Redux current values. Actual one is stored in searchText
  switch (fieldName) {
    case `${namePrefix}.houseNumber`:
      return formatStreet({ houseNumber: searchText, landRegistryNumber, street })
    case `${namePrefix}.landRegistryNumber`:
      return formatStreet({ houseNumber, landRegistryNumber: searchText, street })
    case `${namePrefix}.street`:
      return formatStreet({ houseNumber, landRegistryNumber, street: searchText })
    default:
      return ''
  }
}

export const AddressInputs = ({
  change,
  form,
  getAddressHints,
  namePrefix,
  translate,
  formValues,
}: Props) => {
  const [state, setState] = useState(defaultState)

  const onSuggestionsClearRequested = () => {
    clearTimeout(fetchAddressTimeoutRef)
    setState(defaultState)
  }

  const setAddress = (id: number, value?: string, addresses?: Address[]) => {
    if (fetchAddressRequestId === id) {
      setState({
        searchedAddressText: value || '',
        addresses: addresses || [],
      })
    }
  }

  const fetchAddress = (searchText: string, fieldName: string) => {
    const id = Math.random()
    fetchAddressRequestId = id
    clearTimeout(fetchAddressTimeoutRef)
    fetchAddressTimeoutRef = setTimeout(() => {
      const value = getValue({ fieldName, searchText, formValues, namePrefix })

      if (value) {
        if (value !== state.searchedAddressText) {
          getAddressHints({
            searchedAddressText: value,
            success: (addresses: Address[]) => setAddress(id, value, addresses),
            error: () => setAddress(id),
          })
        }
      } else {
        setState(defaultState)
      }
    }, 300)
  }

  const onSuggestionSelected = (address: Address) => {
    change(form, `${namePrefix}.city`, trim(address.town))
    change(form, `${namePrefix}.houseNumber`, trim(address.orientationNumber))
    change(form, `${namePrefix}.landRegistryNumber`, trim(address.descriptiveNumber))
    change(form, `${namePrefix}.street`, trim(address.street))
    change(form, `${namePrefix}.zip`, trim(address.zip))
  }

  return (
    <>
      <BRow>
        <BCol theme="col-12 col-sm-12 col-xl-6">
          <BAutoComplete
            data={state.addresses}
            messages={{
              label: translate(msg.street),
              placeholder: translate(msg.streetPlaceholder),
            }}
            name={`${namePrefix}.street`}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            onSuggestionSelected={onSuggestionSelected}
            onSuggestionsFetchRequested={fetchAddress}
            testid={`${namePrefix}.street`}
          />
        </BCol>
        <BCol theme="col-12 col-sm-6 col-xl-3">
          <BAutoComplete
            data={state.addresses}
            messages={{
              label: translate(msg.landRegistryNumber),
            }}
            name={`${namePrefix}.landRegistryNumber`}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            onSuggestionSelected={onSuggestionSelected}
            onSuggestionsFetchRequested={fetchAddress}
            testid={`${namePrefix}.landRegistryNumber`}
          />
        </BCol>
        <BCol theme="col-12 col-sm-6 col-xl-3">
          <BAutoComplete
            data={state.addresses}
            messages={{
              label: translate(msg.houseNumber),
            }}
            name={`${namePrefix}.houseNumber`}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            onSuggestionSelected={onSuggestionSelected}
            onSuggestionsFetchRequested={fetchAddress}
            testid={`${namePrefix}.houseNumber`}
          />
        </BCol>
      </BRow>
      <BRow>
        <BCol theme="col-12 col-sm-12 col-xl-8">
          <BTextInput
            autoComplete="chrome-off"
            messages={{ label: translate(msg.city) }}
            name={`${namePrefix}.city`}
            testid={`${namePrefix}.city`}
          />
        </BCol>
        <BCol theme="col-12 col-sm-6 offset-sm-6 col-xl-4 offset-xl-0">
          <BTextInput
            autoComplete="chrome-off"
            messages={{ label: translate(msg.zip) }}
            name={`${namePrefix}.zip`}
            testid={`${namePrefix}.zip`}
          />
        </BCol>
      </BRow>
    </>
  )
}

const connector = connect(
  (state: ReduxState, ownProps: OuterProps) => ({
    formValues: getFormValues(ownProps.form)(state),
  }),
  {
    change,
    getAddressHints: codetableActions.getAddressHints,
  }
)

export default compose(intl, connector)(AddressInputs)
